import { useEffect, useMemo, useState } from 'react';
import { useParcelasPrivadas, useParcelasTecnicas } from '@aosenuma/lib-supabase';

/**
 * Merge runtime de parcelas RAN (GeoJSON estático precacheado) +
 * parcelas privadas del tenant (Supabase via RLS).
 *
 * Mismo patrón que useParcelasLiveMerged de apps/dashboard, pero
 * simplificado para Captura — el equipo no necesita estados live ni
 * cruce con inventario, solo necesita ver las parcelas en el mapa
 * para que el técnico pueda hacer click (RAN) o ver la nueva privada
 * recién cargada con border dashed teal.
 *
 * Cuando `parcelasGeoJSON` se pasa a MapaParcelas, este lo usa en
 * lugar del fetch interno por URL. Por eso conservamos los properties
 * RAW del GeoJSON estático + agregamos las features privadas con
 * `properties.source = 'cliente'` para que ParcelasLayer (sig-viewer)
 * las renderee con border teal dashed.
 *
 * Handoff parcelas privadas v2 §9 — extensión Fix 2.
 */
export function useParcelasMergedCaptura(): {
  data: GeoJSON.FeatureCollection | undefined;
  /**
   * Tierras de uso común enriquecidas con `live_*` (mig 0068). Va a
   * `MapaParcelas.usoComunGeoJSON` — capa separada de parcelas (violeta,
   * toggle propio) pero con el mismo pipeline de gestión: el técnico
   * puede clickear un polígono HUM-UC#### y capturar igual que una parcela.
   */
  usoComun: GeoJSON.FeatureCollection | undefined;
  refetchPrivadas: () => Promise<void>;
} {
  const { data: privadas, refetch: refetchPrivadas } = useParcelasPrivadas();
  // useParcelasTecnicas trae v_parcelas_tecnicas con estado/firmado/irregular
  // de cada parcela (RAN + privadas, gracias al proxy en public.parcelas de
  // mig 0059; + uso común desde mig 0068). Lo usamos para colorear las
  // privadas según gestion real, si no se quedan grises (sin_prospectar
  // default) aunque ya estén contratadas / en_negociacion.
  const { data: tecnicas } = useParcelasTecnicas();
  const [staticFeatures, setStaticFeatures] = useState<GeoJSON.Feature[]>([]);
  const [usoComunFeatures, setUsoComunFeatures] = useState<GeoJSON.Feature[]>([]);

  useEffect(() => {
    let cancel = false;
    fetch('/maps/parcelas_unificado.geojson')
      .then((r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        return r.json() as Promise<{ features?: GeoJSON.Feature[] }>;
      })
      .then((j) => {
        if (!cancel) setStaticFeatures(j.features ?? []);
      })
      .catch(() => {
        if (!cancel) setStaticFeatures([]);
      });
    fetch('/maps/uso_comun.geojson')
      .then((r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        return r.json() as Promise<{ features?: GeoJSON.Feature[] }>;
      })
      .then((j) => {
        if (!cancel) setUsoComunFeatures(j.features ?? []);
      })
      .catch(() => {
        if (!cancel) setUsoComunFeatures([]);
      });
    return () => {
      cancel = true;
    };
  }, []);

  const data = useMemo<GeoJSON.FeatureCollection | undefined>(() => {
    if (!staticFeatures.length && !privadas.length) return undefined;

    // Index de v_parcelas_tecnicas por parcela_id para lookup O(1) de estado.
    const tecnicasMap = new Map<string, { estado?: string; firmado?: boolean; irregular?: boolean }>();
    for (const t of tecnicas) {
      tecnicasMap.set(t.parcela_id, {
        estado: t.estado,
        firmado: t.firmado,
        irregular: t.irregular,
      });
    }

    // Enriquecer TODAS las features (RAN + privadas) con live_* properties.
    // Sin esto, ParcelasLayer activa modo enriched (al recibir geojson prop)
    // y todo se pinta gris porque ['get', 'live_estado'] no resuelve.
    const features: GeoJSON.Feature[] = [];
    for (const ft of staticFeatures) {
      const props = ft.properties ?? {};
      const pid = typeof props.parcela_id === 'string' ? props.parcela_id : '';
      const tec = pid ? tecnicasMap.get(pid) : undefined;
      features.push({
        type: 'Feature',
        geometry: ft.geometry as GeoJSON.Geometry,
        properties: {
          ...props,
          live_estado: tec?.estado ?? 'sin_prospectar',
          live_firmado: tec?.firmado ?? false,
          live_irregular: tec?.irregular ?? false,
          live_reunion_bucket: 'sin_reunion',
        },
      });
    }

    for (const pp of privadas) {
      const g = pp.geom_geojson as GeoJSON.Geometry | null | undefined;
      if (!g || (g.type !== 'Polygon' && g.type !== 'MultiPolygon')) continue;
      const tec = tecnicasMap.get(pp.parcela_id);
      features.push({
        type: 'Feature',
        geometry: g,
        properties: {
          parcela_id: pp.parcela_id,
          municipio: pp.municipio,
          nom_nucleo: pp.nom_nucleo,
          num_parcela: pp.num_parcela,
          area_ha: pp.area_ha,
          source: 'cliente',
          live_estado: tec?.estado ?? 'sin_prospectar',
          live_firmado: tec?.firmado ?? false,
          live_irregular: tec?.irregular ?? false,
          live_reunion_bucket: 'sin_reunion',
        },
      });
    }
    return { type: 'FeatureCollection', features };
  }, [staticFeatures, privadas, tecnicas]);

  // Uso común enriquecido (capa separada — NO se mezcla en `data` para que
  // ParcelasLayer y sus filtros P1/P2/PP no lo clasifiquen como parcela).
  const usoComun = useMemo<GeoJSON.FeatureCollection | undefined>(() => {
    if (!usoComunFeatures.length) return undefined;
    const tecnicasMap = new Map<string, { estado?: string; firmado?: boolean; irregular?: boolean }>();
    for (const t of tecnicas) {
      tecnicasMap.set(t.parcela_id, { estado: t.estado, firmado: t.firmado, irregular: t.irregular });
    }
    const features: GeoJSON.Feature[] = usoComunFeatures.map((ft) => {
      const props = ft.properties ?? {};
      const pid = typeof props.parcela_id === 'string' ? props.parcela_id : '';
      const tec = pid ? tecnicasMap.get(pid) : undefined;
      return {
        type: 'Feature',
        geometry: ft.geometry as GeoJSON.Geometry,
        properties: {
          ...props,
          source: 'RAN_uso_comun',
          live_estado: tec?.estado ?? 'sin_prospectar',
          live_firmado: tec?.firmado ?? false,
          live_irregular: tec?.irregular ?? false,
          live_reunion_bucket: 'sin_reunion',
        },
      };
    });
    return { type: 'FeatureCollection', features };
  }, [usoComunFeatures, tecnicas]);

  return { data, usoComun, refetchPrivadas };
}
